import type { OrigemGmud, Prioridade, StatusGmud } from '../types/gmud'

/** Os critérios da listagem, como a tela os enxerga. */
export interface FiltrosDaLista {
  status: StatusGmud[]
  prioridade: Prioridade[]
  /**
   * Procedência. O padrão da listagem é `['V2']`, e é o que `lerDaUrl` devolve quando a URL não
   * pede procedência nenhuma: sem isso, 1.025 dos 1.027 registros importados — todos escritos
   * "Aprovado" no sistema anterior — tomam a primeira página e se leem como aprovações do comitê,
   * que nunca houve. Quem quer as duas coisas pede as duas (`origem=V2&origem=MIGRADO_V1`).
   *
   * Vazio continua querendo dizer "todas as procedências" para a API — é o filtro do backend —
   * mas não é estado que a URL saiba escrever, porque URL sem `origem` é o padrão. Quem monta os
   * critérios na tela normaliza o vazio para as duas (`CamposDeFiltro`).
   *
   * Quem quer só o histórico pede `MIGRADO_V1` aqui, e não esconde o processo depois de receber a
   * página: filtrar na tela faria o rodapé contar uma coisa e a tabela mostrar outra — o defeito
   * que `GmudConsultaJdbc` existe para não repetir.
   */
  origem: OrigemGmud[]
  chamado: string
  q: string
  /** Página que o usuário vê, começando em 1. A API conta a partir de zero. */
  pagina: number
  tamanho: number
}

export const TAMANHO_PADRAO = 20

/**
 * A procedência que a listagem mostra quando ninguém pede outra: só o processo deste sistema.
 *
 * As 1.027 solicitações trazidas do sistema anterior continuam inteiras e pesquisáveis — o item de
 * menu "Histórico do sistema anterior" (`/gmuds?origem=MIGRADO_V1`) e a caixa de procedência no
 * painel de filtros são os dois caminhos até elas. O que mudou é que agora é preciso pedir.
 */
export const ORIGEM_PADRAO: OrigemGmud[] = ['V2']

/**
 * Se a procedência é exatamente a padrão.
 *
 * É a pergunta que separa "a lista está no recorte de sempre" de "alguém escolheu a procedência",
 * e ela é feita em três lugares: o que a URL escreve (`escreverNaUrl`), o que conta como filtro
 * (`quantidadeDeFiltros`) e o que a tela diz estar mostrando (`LinhaDoRecorte`). Uma função só
 * evita que os três discordem sobre qual é o padrão.
 *
 * @param origem procedências correntes
 * @returns verdadeiro quando é só o processo deste sistema
 */
export function eOrigemPadrao(origem: readonly OrigemGmud[]): boolean {
  return origem.length === 1 && origem[0] === ORIGEM_PADRAO[0]
}

/**
 * A tradução entre a query string da tela e a da API, num lugar só.
 *
 * São dois dialetos parecidos e diferentes: a URL do navegador conta páginas a partir de
 * 1, porque é o que aparece escrito para o usuário; a API conta a partir de zero. Ter as
 * duas conversões lado a lado é o que impede o clássico "a página 2 mostra a 3".
 *
 * @param parametros a query string do navegador
 * @returns os critérios correspondentes
 */
export function lerDaUrl(parametros: URLSearchParams): FiltrosDaLista {
  const origem = parametros.getAll('origem') as OrigemGmud[]
  return {
    status: parametros.getAll('status') as StatusGmud[],
    prioridade: parametros.getAll('prioridade') as Prioridade[],
    // Só o que a URL NÃO diz vira padrão. Quando ela diz alguma coisa — inclusive as duas
    // procedências de uma vez — vale exatamente o que veio: é assim que alguém pede o histórico
    // junto do processo de propósito, e a tela não tem como desfazer esse pedido.
    origem: origem.length > 0 ? origem : [...ORIGEM_PADRAO],
    chamado: parametros.get('chamado') ?? '',
    q: parametros.get('q') ?? '',
    pagina: inteiro(parametros.get('pagina'), 1),
    tamanho: inteiro(parametros.get('tamanho'), TAMANHO_PADRAO),
  }
}

/**
 * Escreve só o que difere do padrão.
 *
 * Um link com `?status=SOLICITADA` é legível e sobrevive a uma mudança de padrão; um com
 * `?status=&prioridade=&chamado=&q=&pagina=1&tamanho=20` não é nenhuma das duas coisas.
 *
 * A procedência padrão entra na mesma regra: `/gmuds` continua sendo o endereço limpo da
 * listagem, e `lerDaUrl` o relê como `['V2']` — ida e volta estável, sem `?origem=V2` grudado
 * na barra de endereço de todo mundo.
 *
 * @param filtros critérios a codificar
 * @returns a query string correspondente
 */
export function escreverNaUrl(filtros: FiltrosDaLista): URLSearchParams {
  const parametros = new URLSearchParams()
  filtros.status.forEach((s) => parametros.append('status', s))
  filtros.prioridade.forEach((p) => parametros.append('prioridade', p))
  if (!eOrigemPadrao(filtros.origem)) {
    filtros.origem.forEach((o) => parametros.append('origem', o))
  }
  if (filtros.chamado.trim()) {
    parametros.set('chamado', filtros.chamado.trim())
  }
  if (filtros.q.trim()) {
    parametros.set('q', filtros.q.trim())
  }
  if (filtros.pagina > 1) {
    parametros.set('pagina', String(filtros.pagina))
  }
  if (filtros.tamanho !== TAMANHO_PADRAO) {
    parametros.set('tamanho', String(filtros.tamanho))
  }
  return parametros
}

/**
 * Os mesmos critérios no formato que `GET /gmuds` espera.
 *
 * Status, prioridade e origem vão como parâmetros repetidos (`status=A&status=B`), que é o que o
 * Spring liga a uma `List<>`. O serializador padrão do axios escreveria `status[]=A`, que
 * chega como filtro vazio — e filtro vazio significa "todos", ou seja, a tela mostraria
 * mais registros do que o usuário pediu sem nenhum erro aparente.
 *
 * @param filtros critérios da tela
 * @returns a query string da consulta
 */
export function paraConsulta(filtros: FiltrosDaLista): URLSearchParams {
  const parametros = escreverNaUrl(filtros)
  // A omissão que `escreverNaUrl` faz é sobre o endereço, não sobre a consulta. Para a API,
  // `origem` ausente quer dizer "todas as procedências" — exatamente o que não pode voltar sem
  // alguém ter pedido. Aqui a procedência vai sempre escrita, padrão inclusive.
  parametros.delete('origem')
  filtros.origem.forEach((o) => parametros.append('origem', o))
  parametros.set('pagina', String(Math.max(filtros.pagina - 1, 0)))
  parametros.set('tamanho', String(filtros.tamanho))
  return parametros
}

function inteiro(valor: string | null, padrao: number): number {
  const numero = Number(valor)
  return Number.isInteger(numero) && numero > 0 ? numero : padrao
}
